import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useAdminMutation } from '../api'
import { fromZonedInput, toZonedInput } from '../zonedInput'
import type { AdminEvent, DuplicationReport } from '../types'
import { NumberField, TextField } from './fields'
import { slugProblem } from './EditionCreateForm'
import { isValidTimeZone } from './editionFields'

type CopyFlag =
  | 'copy_content'
  | 'copy_catalog'
  | 'copy_tickets'
  | 'copy_pricing'
  | 'copy_tables'
  | 'copy_program'
  | 'copy_competition'
  | 'copy_stock'

/** Dans l'ordre où on se pose les questions en préparant l'édition suivante. */
const FLAGS: CopyFlag[] = [
  'copy_content',
  'copy_catalog',
  'copy_tickets',
  'copy_pricing',
  'copy_tables',
  'copy_program',
  'copy_competition',
  'copy_stock',
]

/**
 * Valeurs par défaut identiques à celles du serveur (`EventDuplicate`) : tout
 * suit, sauf le stock. Celui de l'an passé a été bu ; le recopier afficherait
 * des bouteilles qui n'existent plus, et la vente s'arrêterait au mauvais
 * moment — ou ne s'arrêterait pas.
 */
const DEFAULT_FLAGS: Record<CopyFlag, boolean> = {
  copy_content: true,
  copy_catalog: true,
  copy_tickets: true,
  copy_pricing: true,
  copy_tables: true,
  copy_program: true,
  copy_competition: true,
  copy_stock: false,
}

type DuplicateBody = {
  slug: string
  edition_year: number
  starts_at: string
} & Record<CopyFlag, boolean>

/** `sales.open` n'est pas une clé de traduction possible (le point imbrique). */
const RESET_KEYS: Record<string, string> = {
  'sales.open': 'salesOpen',
  'onsite.ordering_enabled': 'onsiteOrdering',
  'countdown.target_at': 'countdown',
}

/** Propose l'année suivante : « masked-night-2026 » → « masked-night-2027 ». */
function suggestSlug(source: AdminEvent, year: number): string {
  const old = String(source.edition_year)
  if (source.slug.includes(old)) return source.slug.replace(old, String(year))
  return `${source.slug}-${year}`
}

/** Même heure murale, un an plus tard, à l'heure de l'édition source. */
function suggestStart(source: AdminEvent): string {
  if (!isValidTimeZone(source.timezone)) return ''
  const wall = toZonedInput(source.starts_at, source.timezone)
  const match = /^(\d{4})(-.*)$/.exec(wall)
  return match ? `${Number(match[1]) + 1}${match[2]}` : wall
}

/**
 * Préparer l'édition suivante à partir de celle-ci.
 *
 * C'est le geste de chaque rentrée : on garde la carte, les billets, les
 * textes, et on repart d'une salle vide. Tout ce qui raconte la soirée passée
 * (commandes, billets vendus, votes, caisse) reste dans l'ancienne : la
 * nouvelle n'en hérite jamais, quelles que soient les cases.
 *
 * L'encadré « ce qui est recopié » n'est pas un texte d'aide décoratif : il
 * dit ce qu'il faudra revérifier — les liens de paiement, les codes promo
 * désactivés — avant de rouvrir la vente.
 */
export function EditionDuplicateForm({
  source,
  eventId,
  onSwitch,
  onCancel,
}: {
  source: AdminEvent
  eventId: string | undefined
  onSwitch: (id: string) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const nextYear = source.edition_year + 1
  const [slug, setSlug] = useState(() => suggestSlug(source, nextYear))
  const [year, setYear] = useState<number | null>(nextYear)
  const [starts, setStarts] = useState(() => suggestStart(source))
  const [flags, setFlags] = useState<Record<CopyFlag, boolean>>(DEFAULT_FLAGS)
  const [report, setReport] = useState<DuplicationReport | null>(null)

  const duplicate = useAdminMutation<DuplicationReport, DuplicateBody>(
    'post',
    () => `/admin/events/${source.id}/duplicate`,
    eventId,
    [['admin-editions'], ['public-event']],
  )

  // Les heures se saisissent dans le fuseau de l'édition source : la nouvelle
  // en hérite, et c'est là que la soirée aura lieu.
  const zone = source.timezone
  const startsIso = isValidTimeZone(zone) ? fromZonedInput(starts, zone) : null

  const problems: string[] = []
  if (slugProblem(slug)) problems.push(t('admin.editions.problem.slug'))
  if (slug === source.slug) problems.push(t('admin.editions.problem.sameSlug'))
  if (year === null || year < 2020 || year > 2100) problems.push(t('admin.editions.problem.year'))
  if (!startsIso) problems.push(t('admin.editions.problem.starts'))

  if (report) return <DuplicationResult report={report} onSwitch={onSwitch} onClose={onCancel} />

  return (
    <div className="rounded-md border border-line-subtle bg-bg-base/40 p-4 sm:p-5">
      <h3 className="font-display text-heading-sm text-content-primary">
        {t('admin.editions.duplicate.title', { slug: source.slug })}
      </h3>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.editions.duplicate.intro')}</p>

      <div className="mt-4 grid gap-5 sm:grid-cols-2">
        <TextField
          label={t('admin.editions.slug')}
          hint={t('admin.editions.slugHint')}
          value={slug}
          onChange={(value) => setSlug(value.trim().toLowerCase())}
        />
        <NumberField
          label={t('admin.editions.year')}
          value={year}
          min={2020}
          max={2100}
          onChange={setYear}
        />
        <TextField
          label={t('admin.event.starts')}
          hint={t('admin.event.whenHint', { timezone: zone })}
          type="datetime-local"
          value={starts}
          onChange={setStarts}
        />
      </div>

      <fieldset className="mt-5">
        <legend className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.editions.duplicate.what')}
        </legend>
        <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {FLAGS.map((flag) => (
            <li key={flag} className={flag === 'copy_stock' ? 'sm:col-span-2' : undefined}>
              <label
                className={cn(
                  'flex items-start gap-3 rounded-md border px-3 py-2.5',
                  flags[flag] ? 'border-champagne-400/40' : 'border-line-subtle',
                )}
              >
                <input
                  type="checkbox"
                  checked={flags[flag]}
                  onChange={() => setFlags((current) => ({ ...current, [flag]: !current[flag] }))}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
                />
                <span className="min-w-0">
                  <span className="block text-sm text-content-primary">
                    {t(`admin.editions.duplicate.flag.${flag}`)}
                  </span>
                  {flag === 'copy_stock' ? (
                    <span className="mt-0.5 block text-xs text-content-muted">
                      {t('admin.editions.duplicate.stockHint')}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
        {/* Les conséquences des cases décochées, au moment où on les décoche :
            découvertes dans le rapport, elles arrivent trop tard. */}
        {!flags.copy_catalog ? (
          <p className="mt-2 rounded-md border border-state-warning/30 bg-state-warning/5 px-3 py-2 text-xs text-content-secondary">
            {t('admin.editions.duplicate.noCatalogWarning')}
          </p>
        ) : null}
      </fieldset>

      <DuplicationExplainer />

      {problems.length > 0 ? (
        <ul className="mt-4 space-y-1 text-xs text-content-muted">
          {problems.map((problem) => (
            <li key={problem}>· {problem}</li>
          ))}
        </ul>
      ) : null}

      {duplicate.error ? (
        <p className="mt-3 text-sm text-state-danger" role="alert">
          {duplicate.error instanceof ApiError
            ? duplicate.error.message
            : t('admin.editions.genericError')}
        </p>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button
          size="sm"
          disabled={problems.length > 0 || duplicate.isPending}
          onClick={() => {
            if (!startsIso || year === null) return
            duplicate.mutate(
              { slug, edition_year: year, starts_at: startsIso, ...flags },
              { onSuccess: setReport },
            )
          }}
        >
          {duplicate.isPending ? t('state.loading') : t('admin.editions.duplicate.submit')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('admin.cancel')}
        </Button>
      </div>
    </div>
  )
}

/** Ce que la duplication fait toujours, ne fait jamais, et remet à zéro. */
function DuplicationExplainer() {
  const { t } = useTranslation()
  const sections = ['always', 'reset', 'never', 'pricing', 'catalogLinks'] as const
  return (
    <details className="mt-5 rounded-md border border-line-subtle px-3 py-2.5">
      <summary className="cursor-pointer text-sm text-content-primary">
        {t('admin.editions.duplicate.explain.title')}
      </summary>
      <dl className="mt-3 space-y-3">
        {sections.map((section) => (
          <div key={section}>
            <dt className="text-caption uppercase tracking-wide text-content-muted">
              {t(`admin.editions.duplicate.explain.${section}.title`)}
            </dt>
            <dd className="mt-0.5 text-xs text-content-secondary">
              {t(`admin.editions.duplicate.explain.${section}.body`)}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  )
}

/**
 * Le rapport, ligne par ligne, tel que le serveur l'a compté.
 *
 * Les éléments écartés sont dits en phrases : « 3 bouteilles n'ont pas suivi »
 * dit ce qu'il faut aller reprendre, un « table_included_items: 3 » ne le dit
 * à personne.
 */
function DuplicationResult({
  report,
  onSwitch,
  onClose,
}: {
  report: DuplicationReport
  onSwitch: (id: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const copied = Object.entries(report.copied).filter(([, count]) => count > 0)
  const skipped = Object.entries(report.skipped).filter(([, count]) => count > 0)

  return (
    <div className="rounded-md border border-state-success/30 bg-state-success/5 p-4 sm:p-5">
      <h3 className="font-display text-heading-sm text-content-primary">
        {t('admin.editions.report.title', { slug: report.event.slug })}
      </h3>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.editions.report.intro')}</p>

      <h4 className="mt-4 text-caption uppercase tracking-wide text-content-muted">
        {t('admin.editions.report.copiedTitle')}
      </h4>
      {copied.length > 0 ? (
        <ul className="mt-1.5 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          {copied.map(([key, count]) => (
            <li key={key} className="flex justify-between gap-3 text-sm text-content-primary">
              <span className="min-w-0">
                {t(`admin.editions.report.copied.${key}`, { defaultValue: key })}
              </span>
              <span className="shrink-0 tabular-nums text-content-secondary">{count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1.5 text-sm text-content-muted">{t('admin.editions.report.nothingCopied')}</p>
      )}

      {skipped.length > 0 ? (
        <>
          <h4 className="mt-4 text-caption uppercase tracking-wide text-content-muted">
            {t('admin.editions.report.skippedTitle')}
          </h4>
          <ul className="mt-1.5 space-y-1">
            {skipped.map(([key, count]) => (
              <li key={key} className="text-sm text-state-warning">
                {t(`admin.editions.report.skipped.${key}`, {
                  count,
                  defaultValue: `${key} : ${count}`,
                })}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {report.reset.length > 0 ? (
        <>
          <h4 className="mt-4 text-caption uppercase tracking-wide text-content-muted">
            {t('admin.editions.report.resetTitle')}
          </h4>
          <ul className="mt-1.5 space-y-1">
            {report.reset.map((code) => (
              <li key={code} className="text-sm text-content-secondary">
                {RESET_KEYS[code]
                  ? t(`admin.editions.report.reset.${RESET_KEYS[code]}`)
                  : code}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <p className="mt-4 text-xs text-content-muted">{t('admin.editions.report.checkBefore')}</p>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button size="sm" onClick={() => onSwitch(report.event.id)}>
          {t('admin.editions.openNew')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('admin.editions.close')}
        </Button>
      </div>
    </div>
  )
}
